import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaEye, FaCopy, FaCheckCircle, FaToggleOff, FaTimes, FaUserCircle, FaShoppingCart, FaCalendarAlt } from 'react-icons/fa';
import './ReferralLinkManagement.css';
// ReferralLinkDetailsModal будет заменен на компонент, отображаемый на странице

// Компонент для отображения деталей ссылки (замена модального окна)
const ReferralLinkDetailsView = ({ linkId, onClose, botUsername }) => {
  const { user } = useAuth();
  const [linkDetails, setLinkDetails] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!linkId) return;
    setLoading(true);
    setError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const linkResponse = await fetch(`/api/referrals/${linkId}`, {
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!linkResponse.ok) {
        const errData = await linkResponse.json();
        throw new Error(errData.message || `Ошибка HTTP при загрузке ссылки: ${linkResponse.status}`);
      }
      const linkData = await linkResponse.json();
      setLinkDetails(linkData);

      const usersResponse = await fetch(`/api/referrals/${linkId}/users`, {
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!usersResponse.ok) {
        const errData = await usersResponse.json();
        throw new Error(errData.message || `Ошибка HTTP при загрузке пользователей: ${usersResponse.status}`);
      }
      const usersData = await usersResponse.json();
      setReferredUsers(usersData);

    } catch (err) {
      console.error("Ошибка при загрузке деталей реферальной ссылки:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [linkId, user]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getFullReferralLink = (code) => {
    const currentBotUsername = botUsername || localStorage.getItem('botUsername') || 'YOUR_BOT_USERNAME';
    return `https://t.me/${currentBotUsername}?start=${code}`;
  };

  if (loading) {
    return <div className="loading-state"><div className="loading-spinner"></div><p>Загрузка деталей...</p></div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <p>Ошибка: {error}</p>
        <button onClick={onClose} className="admin-button">Назад к списку</button>
      </div>
    );
  }

  if (!linkDetails) {
    return <div className="empty-state"><p>Не удалось загрузить детали ссылки.</p><button onClick={onClose} className="admin-button">Назад к списку</button></div>;
  }

  return (
    <div className="referral-details-view content-form-container"> {/* Используем стили как у ContentForm */}
      <div className="form-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Детали: {linkDetails.name}</h2>
        <button onClick={onClose} className="admin-button" style={{padding: '8px 12px'}}>
          <FaTimes /> Закрыть
        </button>
      </div>
      
      <div className="details-section card-style" style={{ marginBottom: '20px', padding: '15px', borderRadius: '8px' }}>
        <p><strong>Код:</strong> {linkDetails.code}</p>
        <p><strong>Полная ссылка:</strong> <a href={getFullReferralLink(linkDetails.code)} target="_blank" rel="noopener noreferrer">{getFullReferralLink(linkDetails.code)}</a></p>
        <p><strong>Описание:</strong> {linkDetails.description || 'Нет'}</p>
        <p><strong>Статус:</strong> {linkDetails.isActive ? 'Активна' : 'Неактивна'}</p>
        <p><strong>Клики:</strong> {linkDetails.clicks || 0}</p>
        <p><strong>Новых:</strong> {linkDetails.registrations || 0}</p>
        <p><strong>Общий доход:</strong> {linkDetails.totalIncome ? linkDetails.totalIncome.toFixed(2) : '0.00'} ₽</p>
        <p><strong>Создана:</strong> {formatDate(linkDetails.createdAt)}</p>
        {linkDetails.adminCreator && <p><strong>Создал:</strong> {linkDetails.adminCreator.firstName} (@{linkDetails.adminCreator.username || 'N/A'})</p>}
      </div>

      <h3>Зарегистрированные пользователи ({referredUsers.length}):</h3>
      {referredUsers.length > 0 ? (
        <ul className="referred-users-list card-style" style={{ listStyle: 'none', padding: '15px', borderRadius: '8px' }}>
          {referredUsers.map(refUser => (
            <li key={refUser._id || refUser.telegramId} className="referred-user-item" style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--border-color)' }}>
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                <FaUserCircle className="user-icon" style={{ marginRight: '8px', fontSize: '1.2em' }} />
                <span>{refUser.firstName || 'N/A'} (@{refUser.username || refUser.telegramId})</span>
              </div>
              <div className="user-stats" style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                <span style={{ marginRight: '15px' }}><FaCalendarAlt style={{ marginRight: '5px' }} /> Зарегистрирован: {formatDate(refUser.createdAt)}</span>
                <span><FaShoppingCart style={{ marginRight: '5px' }} /> Покупок: {refUser.purchaseCount || 0}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty-state-text">По этой ссылке пока нет зарегистрированных пользователей.</p>
      )}
    </div>
  );
};


// Компонент для формы создания/редактирования ссылки
const ReferralLinkForm = ({ link, onSave, onCancel, loading: formLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    isActive: true,
    incomePercentage: 0,
  });

  useEffect(() => {
    if (link) {
      setFormData({
        name: link.name || '',
        description: link.description || '',
        code: link.code || '',
        isActive: link.isActive !== undefined ? link.isActive : true,
        incomePercentage: link.incomePercentage || 0,
      });
    } else {
      // Для новой ссылки генерируем случайный код
      setFormData(prev => ({
        ...prev,
        name: '',
        description: '',
        code: Math.random().toString(36).substring(2, 10), // Генерация случайного кода
        isActive: true,
        incomePercentage: 0,
      }));
    }
  }, [link]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'incomePercentage' ? parseInt(value, 10) : value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      alert('Название и код ссылки обязательны для заполнения.');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="content-form-container"> {/* Используем стили как у ContentForm */}
      <h2>{link ? 'Редактировать реферальную ссылку' : 'Создать реферальную ссылку'}</h2>
      <form onSubmit={handleSubmit} className="content-form">
        <div className="form-group">
          <label htmlFor="name">Название (для админа):</label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="description">Описание (необязательно):</label>
          <textarea id="description" name="description" value={formData.description} onChange={handleChange}></textarea>
        </div>
        <div className="form-group">
          <label htmlFor="code">Код ссылки (уникальный, после ?start=):</label>
          <input type="text" id="code" name="code" value={formData.code} onChange={handleChange} required 
                 disabled={!!link} // Запрещаем редактирование кода для существующих ссылок
          />
          {link && <small>Код существующей ссылки изменить нельзя.</small>}
          {!link && <small>Сгенерирован автоматически, можно изменить.</small>}
        </div>
        <div className="form-group">
          <label htmlFor="incomePercentage">Процент дохода от покупок рефералов (%):</label>
          <input type="number" id="incomePercentage" name="incomePercentage" value={formData.incomePercentage} onChange={handleChange} min="0" max="100" step="1" />
        </div>
        <div className="form-group checkbox-group">
          <div className="checkbox-item">
            <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} />
            <label htmlFor="isActive">Активна</label>
          </div>
        </div>
        <div className="form-actions">
          <button type="button" className="admin-button" onClick={onCancel} disabled={formLoading}>
            Отмена
          </button>
          <button type="submit" className="admin-button" disabled={formLoading}>
            {formLoading ? 'Сохранение...' : (link ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </form>
    </div>
  );
};


const ReferralLinkManagement = ({ botUsername }) => {
  const { user } = useAuth();
  const [referralLinks, setReferralLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [viewMode, setViewMode] = useState('list'); // 'list', 'form', 'details'
  const [editingLink, setEditingLink] = useState(null); // Для редактирования
  const [selectedLinkIdForDetails, setSelectedLinkIdForDetails] = useState(null); // Для деталей

  const fetchReferralLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch('/api/referrals', {
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || `Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setReferralLinks(data);
    } catch (err) {
      console.error("Ошибка при загрузке реферальных ссылок:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.telegramId && viewMode === 'list') { // Загружаем только если мы в режиме списка
      fetchReferralLinks();
    }
  }, [user, fetchReferralLinks, viewMode]);


  const handleCreateNew = () => {
    setEditingLink(null);
    setViewMode('form');
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setViewMode('form');
  };

  const handleSaveLink = async (formDataToSave) => {
    setLoading(true);
    const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
    const url = editingLink ? `/api/referrals/${editingLink._id}` : '/api/referrals';
    const method = editingLink ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId,
        },
        body: JSON.stringify(formDataToSave),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Не удалось сохранить ссылку');
      }
      await response.json();
      setViewMode('list');
      fetchReferralLinks(); 
      alert(`Ссылка успешно ${editingLink ? 'обновлена' : 'создана'}!`);
    } catch (err) {
      console.error("Ошибка при сохранении ссылки:", err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelForm = () => {
    setViewMode('list');
    setEditingLink(null);
  }

  const handleDelete = async (linkId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту реферальную ссылку?')) return;
    setLoading(true);
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const response = await fetch(`/api/referrals/${linkId}`, {
        method: 'DELETE',
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Не удалось удалить ссылку');
      }
      fetchReferralLinks(); 
      alert('Ссылка успешно удалена.');
    } catch (err) {
      console.error("Ошибка при удалении ссылки:", err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyLink = (code) => {
    const currentBotUsername = botUsername || localStorage.getItem('botUsername') || 'YOUR_FALLBACK_BOT_USERNAME';
    if (currentBotUsername === 'YOUR_FALLBACK_BOT_USERNAME') {
      alert('Имя бота не определено. Невозможно скопировать ссылку.');
      return;
    }
    const link = `https://t.me/${currentBotUsername}?start=${code}`;
    navigator.clipboard.writeText(link)
      .then(() => alert('Ссылка скопирована в буфер обмена!'))
      .catch(err => alert('Не удалось скопировать ссылку.'));
  };

  const handleViewDetails = (linkId) => {
    setSelectedLinkIdForDetails(linkId);
    setViewMode('details');
  };

  const handleCloseDetails = () => {
    setViewMode('list');
    setSelectedLinkIdForDetails(null);
  };

  if (viewMode === 'form') {
    return <ReferralLinkForm link={editingLink} onSave={handleSaveLink} onCancel={handleCancelForm} loading={loading} />;
  }

  if (viewMode === 'details') {
    return <ReferralLinkDetailsView linkId={selectedLinkIdForDetails} onClose={handleCloseDetails} botUsername={botUsername} />;
  }

  // viewMode === 'list'
  return (
    <div className="tab-content-item referral-link-management"> 
      <div className="content-header">
        {/* Заголовок удален */}
        <button onClick={handleCreateNew} className="add-button"> 
          <FaPlus /> Создать ссылку
        </button>
      </div>

      {loading && referralLinks.length === 0 ? (
        <div className="loading-state"><div className="loading-spinner"></div><p>Загрузка ссылок...</p></div>
      ) : error ? (
        <div className="error-message">Ошибка при загрузке: {error}</div>
      ) : referralLinks.length === 0 ? (
        <div className="empty-state"><p>Реферальные ссылки еще не созданы.</p></div>
      ) : (
        <div className="admin-table-wrapper content-list">
          {referralLinks.map(link => (
            <div key={link._id} className="content-item">
              <div className="content-item-header">
                <div className="content-title">
                  <h3>{link.name}</h3>
                  <div className="content-meta">
                    <div>Код: {link.code}</div>
                    <div>Клики: {link.clicks || 0} • Новых: {link.registrations || 0}</div>
                    <div>Доход: {link.totalIncome ? link.totalIncome.toFixed(2) : '0.00'} ₽</div>
                  </div>
                </div>
                <div className="content-status">
                  {link.isActive ? (
                    <span className="status-icon published" title="Активна">
                      <FaCheckCircle />
                    </span>
                  ) : (
                    <span className="status-icon draft" title="Неактивна">
                      <FaToggleOff />
                    </span>
                  )}
                </div>
              </div>
              {link.description && <p className="referral-description" style={{fontSize: '0.9em', color: 'var(--text-secondary)', margin: '5px 0'}}>{link.description}</p>}
              <div className="content-actions">
                <div className="button-row">
                  <button 
                    onClick={() => handleCopyLink(link.code)} 
                    className="admin-button"
                    title="Копировать ссылку"
                  >
                    <FaCopy /> Копировать
                  </button>
                  <button 
                    onClick={() => handleViewDetails(link._id)} 
                    className="admin-button"
                    title="Посмотреть детали"
                  >
                    <FaEye /> Детали
                  </button>
                </div>
                <div className="button-row">
                  <button 
                    onClick={() => handleEdit(link)} 
                    className="admin-button" // .edit специфичный цвет убран, будет серым
                    title="Редактировать"
                  >
                    <FaEdit /> Редактировать
                  </button>
                  <button 
                    onClick={() => handleDelete(link._id)} 
                    className="admin-button admin-button-delete"
                    title="Удалить"
                  >
                    <FaTrash /> Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferralLinkManagement;
